import {
    Box,
    Button,
    Heading,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList
} from '@chakra-ui/react'
import { Icon } from 'components/atoms'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { InstitutionDomain } from 'domains/InstitutionDomain'
import { useCacheFilters } from 'hooks/useCacheFilters'
import { useInstitutions } from 'hooks/useInstitutions'
import jwt_decode from 'jwt-decode'
import { AuthTokens, InstitutionsResponseDTO } from 'models'
import {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useLocation } from 'react-router-dom'
import { SESSION_KEYS } from 'utils/sessionKeys'
import {
    getFromSessionStorage,
    removeFromSessionStorage
} from 'utils/sessionStorage'
import { useDebounce } from 'utils/useDebounce'

export const InstitutionAutocomplete = () => {
    const { currentUser, isBackoffice } = useAuthContext()
    const inputRef = useRef<any>(null)
    const decodedIdToken =
        currentUser && (jwt_decode(currentUser.idToken) as AuthTokens)
    const [headerSelect, setHeaderSelect] = useState(false)
    const { pathname } = useLocation()
    const [loading, setLoading] = useState(true)

    const withCache =
        pathname.includes('relatorio') || pathname.includes('contratos')

    const { getCacheFilters } = useCacheFilters()

    const {
        institutions,
        setInstitution,
        setInstitutions,
        currentInstitution,
        setCurrentAgent,
        setAgents,
        getInstitutions,
        setLoadingInstitution
    } = useInstitutionsContext()

    const { getInstitutionsList, institutionOptionAll } = useInstitutions()

    const routesToDisableAutocomplete = [
        '/instituicoes',
        '/veiculos',
        '/agentes-financeiros',
        '/gestao-taxas',
        '/contratos/'
    ]
    const hideInstitutionAutocomplete = routesToDisableAutocomplete.some(
        route => pathname.startsWith(route)
    )

    const setInstuitionByCache = useCallback(() => {
        const cacheFilters = getCacheFilters()

        if (!cacheFilters) return

        const { currentInstitution, currentAgent, institutions, agents } =
            cacheFilters

        setInstitutions(institutions)
        setInstitution(currentInstitution)
        setLoading(false)
        setLoadingInstitution(false)
        setAgents(agents)
        setCurrentAgent(currentAgent)
    }, [setInstitutions, setInstitution, setAgents, setCurrentAgent])
    const { cleanCacheFilters } = useCacheFilters()

    const checkCurrentInstituitionToReset = () => {
        const routesWithOptionAllInstutitions = ['relatorio']
        const currentInstitution = getFromSessionStorage(
            SESSION_KEYS.CURRENT_INSTITUTION
        )
        if (
            currentInstitution?.razaoSocial === 'Todos' &&
            !routesWithOptionAllInstutitions.some(item =>
                window.location.href.includes(item)
            )
        ) {
            removeFromSessionStorage(SESSION_KEYS.CURRENT_INSTITUTION)
        }

        if (
            currentInstitution?.razaoSocial !== 'Todos' &&
            routesWithOptionAllInstutitions.some(item =>
                window.location.href.includes(item)
            )
        ) {
            removeFromSessionStorage(SESSION_KEYS.CURRENT_INSTITUTION)
        }
    }

    useEffect(() => {
        checkCurrentInstituitionToReset()

        if (hideInstitutionAutocomplete) return

        const cacheFilters = getFromSessionStorage('cache-filters') ?? null
        const currentURL = window.location.href

        if (cacheFilters && withCache) {
            if (
                currentURL.includes(cacheFilters.from) ||
                currentURL.includes(cacheFilters.to)
            ) {
                setInstuitionByCache()
                return
            } else {
                cleanCacheFilters()
            }
        }

        if (currentUser?.idToken) {
            fetchInstitutions()
        }
    }, [currentUser?.idToken, pathname])

    const financialInstitution = useMemo(
        () => institutions.map(institution => institution.razaoSocial),
        [institutions]
    )

    const handleUserType = () => {
        if (decodedIdToken?.UFDetran) {
            setHeaderSelect(true)
            return 'Todos'
        }
        if (isBackoffice) {
            return currentInstitution && currentInstitution.razaoSocial
        } else {
            return financialInstitution
        }
    }

    const fetchInstitutions = async (razaoSocial?: string) => {
        try {
            setLoading(true)
            const institutionDomain = new InstitutionDomain()
            await institutionDomain.fetchInstitutionsAutocomplete(
                {
                    razaoSocial: razaoSocial ?? ''
                },
                {
                    setInstitutions,
                    setInstitution,
                    institutionOptionAll
                }
            )
        } finally {
            setLoading(false)
        }
    }

    const clearInput = () => {
        if (inputRef.current) {
            inputRef.current.value = ''
        }

        getInstitutions()
    }

    const handleSelectIf = useCallback(
        (isOpen: boolean) => {
            if (institutions?.length) {
                return <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} />
            }
        },
        [isBackoffice, institutions]
    )

    const handleInputChange = useDebounce(
        (e: ChangeEvent<HTMLInputElement>) => {
            const razaoSocial = e.target.value.toLowerCase().trim()
            fetchInstitutions(razaoSocial)
        }
    )

    const onSelectInstitution = (institution: InstitutionsResponseDTO) => {
        setInstitution(institution)
        if (inputRef.current.value) {
            clearInput()
        }
    }

    return (
        <>
            {!hideInstitutionAutocomplete && (
                <Menu data-testid="header-perfil-menu">
                    {({ isOpen }) => (
                        <>
                            <MenuButton
                                data-testid="header-perfil-button"
                                size="md"
                                colorScheme="white"
                                color="inherit"
                                shadow="md"
                                w={{ base: 'full', lg: 'auto' }}
                                as={Button}
                                rightIcon={handleSelectIf(isOpen)}
                                isActive={isOpen}
                                isLoading={loading}
                                isDisabled={headerSelect}
                            >
                                {handleUserType()}
                            </MenuButton>
                            {institutions?.length ? (
                                <MenuList
                                    data-testid="header-perfil-list"
                                    zIndex="dropdown"
                                >
                                    <Box px="4">
                                        <Box textAlign="center" mt="8">
                                            <Heading
                                                as="h2"
                                                size="md"
                                                fontWeight="bold"
                                            >
                                                Selecionar instituição
                                                Financeira
                                            </Heading>

                                            <Input
                                                mb="4"
                                                placeholder="Buscar pelo nome"
                                                variant="flushed"
                                                onChange={handleInputChange}
                                                ref={inputRef}
                                            />
                                        </Box>
                                        <Box overflowY="auto" maxHeight="300px">
                                            {getInstitutionsList().map(
                                                (institution, key) => (
                                                    <MenuItem
                                                        data-testid={`header-perfil-item-${key}`}
                                                        key={key}
                                                        onClick={() => {
                                                            onSelectInstitution(
                                                                institution
                                                            )
                                                        }}
                                                    >
                                                        {
                                                            institution.razaoSocial
                                                        }
                                                    </MenuItem>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                </MenuList>
                            ) : (
                                <MenuList
                                    data-testid="header-perfil-list"
                                    zIndex="dropdown"
                                >
                                    <Box px="4">
                                        <Box textAlign="center" mt="8">
                                            <Heading
                                                as="h2"
                                                size="md"
                                                fontWeight="bold"
                                            >
                                                Selecionar instituição
                                                Financeira
                                            </Heading>

                                            <Input
                                                mb="4"
                                                placeholder="Buscar pelo nome"
                                                variant="flushed"
                                                onChange={handleInputChange}
                                            />
                                        </Box>
                                        <MenuItem isDisabled>
                                            Nenhum item encontrado.
                                        </MenuItem>
                                    </Box>
                                </MenuList>
                            )}
                        </>
                    )}
                </Menu>
            )}
        </>
    )
}
